import { Send } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { testTextAgentPath } from "@/routes"

// IMP-360 rework: the "Text your agent" tab — a practice SMS thread where the
// business replies as the lead and the agent answers from its ACTUAL
// configured prompt (POST test_text). Practice only: nothing is sent. The
// thread opens with the real first-touch text (the exact message SendText
// would deliver), fetched by posting an empty history.

interface ChatMessage {
  from: "agent" | "lead"
  body: string
}

async function fetchReply(
  agentUid: string,
  messages: ChatMessage[],
): Promise<string> {
  const csrfToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  const response = await fetch(testTextAgentPath({ id: agentUid }), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken ?? "",
    },
    credentials: "same-origin",
    body: JSON.stringify({ messages }),
  })
  const body = (await response.json().catch(() => ({}))) as {
    reply?: string
    error?: string
  }
  if (!response.ok || !body.reply) {
    throw new Error(body.error ?? "Couldn't get a reply.")
  }
  return body.reply
}

export function TextSimulator({ agentUid }: { agentUid: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState("")
  // Starts true: the thread opens by fetching the agent's first-touch text.
  const [typing, setTyping] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Guards the one-time seed against StrictMode double-mount.
  const seeded = useRef(false)

  // Open the thread with the agent's real first-touch text.
  useEffect(() => {
    if (seeded.current) return
    seeded.current = true
    fetchReply(agentUid, [])
      .then((reply) => setMessages([{ from: "agent", body: reply }]))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't get a reply."),
      )
      .finally(() => setTyping(false))
  }, [agentUid])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages, typing])

  function send() {
    const body = draft.trim()
    if (!body || typing) return
    const thread: ChatMessage[] = [...messages, { from: "lead", body }]
    setMessages(thread)
    setDraft("")
    setError(null)
    setTyping(true)
    fetchReply(agentUid, thread)
      .then((reply) =>
        setMessages((m) => [...m, { from: "agent", body: reply }]),
      )
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't get a reply."),
      )
      .finally(() => setTyping(false))
  }

  return (
    <section>
      <div className="border-border overflow-hidden rounded-lg border">
        <div
          ref={scrollRef}
          className="flex h-80 flex-col gap-3 overflow-y-auto p-4"
        >
          {messages.map((m, i) => (
            <Bubble key={i} message={m} />
          ))}
          {typing && <TypingBubble />}
          {error && <p className="text-destructive text-xs">{error}</p>}
        </div>
        <div className="flex items-center gap-2 border-t p-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") send()
            }}
            placeholder="Type a reply as the lead…"
            className="border-0 shadow-none focus-visible:ring-0"
          />
          <Button size="icon" onClick={send} disabled={!draft.trim() || typing}>
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </section>
  )
}

function Bubble({ message }: { message: ChatMessage }) {
  const isLead = message.from === "lead"
  return (
    <div className={cn("flex gap-3", isLead && "flex-row-reverse")}>
      <div
        className={cn(
          "max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
          isLead
            ? "bg-primary text-primary-foreground rounded-tr-sm"
            : "bg-muted text-foreground rounded-tl-sm",
        )}
      >
        {message.body}
      </div>
    </div>
  )
}

function TypingBubble() {
  return (
    <div className="flex gap-3">
      <div className="bg-muted text-muted-foreground flex items-center gap-1 rounded-2xl rounded-tl-sm px-4 py-3">
        <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-current" />
      </div>
    </div>
  )
}
